"use strict";
/**
 * Single source of truth for classifying a lead `Activity` row as automation
 * noise, shared by the backend writers and the frontend timeline filter.
 *
 * Classification is an EXPLICIT REGISTRY, not a prefix heuristic. A prefix
 * heuristic is what broke the FLOSEAI-462 "hide automation" toggle: the filter
 * matched `voice.call.` while the rows that leaked were `voice_call_completed`,
 * `voice_call_status_changed` and `voice.call_analyzed`. Widening the prefix to
 * `voice.` would then have swallowed `voice.consent_recorded` and
 * `voice.phone_number.*`, which are compliance and configuration records that
 * must stay visible.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUTOMATION_CATEGORY_VALUES = exports.ALL_AUTOMATION_ACTIVITY_TYPES = exports.LEGACY_AUTOMATION_ACTIVITY_TYPES = exports.AUTOMATION_ACTIVITY_TYPES = exports.ACTIVITY_CATEGORY = void 0;
exports.isAutomationActivityType = isAutomationActivityType;
exports.ACTIVITY_CATEGORY = {
    AUTOMATION: 'automation',
    SKIP: 'skip',
};
/**
 * Canonical dotted activity types, kept in step with `BUSINESS_EVENTS` in
 * `backend-nest/src/common/events/business-events.ts`. The pairing is enforced
 * by `business-events.spec.ts`, so adding an automation event there without
 * registering it here fails the backend suite.
 */
exports.AUTOMATION_ACTIVITY_TYPES = [
    'automation.skipped',
    'agent.execution_started',
    'agent.execution_completed',
    'agent.execution_failed',
    'agent.execution_cancelled',
    'chat.run.cancelled',
    'chat.tool.completed',
    'chat.tool.skipped',
    'enrichment.status',
    'enrichment.actor_failed',
    'enrichment.partial_success',
    'enrichment.failed',
    'enrichment.completed',
    'enrichment.contact_analysis_fallback',
    'enrichment.side_effects_failed',
    'voice.call.started',
    'voice.call.completed',
    // Underscore separator, unlike its `voice.call.*` siblings. Renaming it would
    // break Mixpanel event continuity, so it is registered explicitly instead.
    'voice.call_analyzed',
    'voice.call_triggered_from_enrichment',
];
/**
 * Snake_case types written by direct `prisma.activity.create` calls that
 * predate the dotted taxonomy. Registered so historical rows filter
 * identically without a data migration (the FLOSEAI-462 zero-migration design).
 * New writers must use `AuditService` with a canonical type instead.
 */
exports.LEGACY_AUTOMATION_ACTIVITY_TYPES = [
    'voice_call_status_changed',
    'voice_call_completed',
    'voice_message_sent',
    'auto_call_completed',
    'lead_enriched',
    'agent_execution_cancelled',
    'agent_pipeline_cancelled',
    // One per BaseAgent subclass, written from `activityType: this.activityType`
    // in `base.agent.ts`. Because the value is a subclass field override rather
    // than a literal at the write site, a grep for `activityType: '<literal>'`
    // does not find them — which is how the whole family was missed and left
    // leaking through the timeline's automation filter. Adding an agent without
    // registering it here fails `activity-type-registry.spec.ts`, which reads the
    // agent sources rather than trusting a hand-maintained list.
    'agent_lost_analysis',
    'agent_enrichment',
    'agent_sentiment',
    'agent_follow_up',
    'agent_first_contact',
    'agent_qualification_deep',
    'agent_prospect_intake',
    'agent_won_onboarding',
    'agent_proposal_prep',
    'agent_qualification',
];
/**
 * The full registry as a flat list, for consumers that filter in a query
 * language instead of JS — the Prisma `activityType: { in: [...] }` clause
 * behind `GET /leads/:id/activities` cannot call the predicate per row.
 */
exports.ALL_AUTOMATION_ACTIVITY_TYPES = [
    ...exports.AUTOMATION_ACTIVITY_TYPES,
    ...exports.LEGACY_AUTOMATION_ACTIVITY_TYPES,
];
/** Values `metadata.category` may hold to mark a row as automation noise. */
exports.AUTOMATION_CATEGORY_VALUES = [
    exports.ACTIVITY_CATEGORY.AUTOMATION,
    exports.ACTIVITY_CATEGORY.SKIP,
];
const AUTOMATION_REGISTRY = new Set(exports.ALL_AUTOMATION_ACTIVITY_TYPES);
const AUTOMATION_CATEGORIES = new Set(exports.AUTOMATION_CATEGORY_VALUES);
/**
 * True when an activity is machine-generated execution noise rather than a
 * human or compliance-relevant event.
 *
 * `metadata.category` wins over the registry so a new writer can opt in without
 * a release of this package; the registry covers rows written before the
 * discriminator existed.
 */
function isAutomationActivityType(activityType, metadata) {
    const category = metadata?.category;
    if (typeof category === 'string' && AUTOMATION_CATEGORIES.has(category)) {
        return true;
    }
    return AUTOMATION_REGISTRY.has(activityType);
}
